import {
  Box,
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import Form from 'components/Form';
import { getCollegeList } from 'apis/admin';
import { postRecoverEmail } from 'apis/account';
import useNotification from 'hooks/useNotification';
import MuiAutocomplete from 'routes/SignUp/components/MuiAutocomplete';

interface Props {
  title: string;
  isOpen?: boolean;
  maxWidth?: Breakpoint;
  onClose: () => void;
  onSwitchToPasswordFind?: () => void;
}

interface ICollegeOption {
  college_id: number;
  college_name: string;
}

type Step = 'input' | 'success' | 'notfound';

function Confirm(props: Props) {
  const {
    isOpen = false,
    maxWidth = 'xs',
    onClose,
    onSwitchToPasswordFind,
  } = props;
  const theme = useTheme();
  const navigate = useNavigate();
  const { onFail } = useNotification();
  const primaryDark = theme.palette.primary.dark;
  const primaryLight = theme.palette.primary.light;

  const [step, setStep] = useState<Step>('input');
  const [userName, setUserName] = useState('');
  const [college, setCollege] = useState<ICollegeOption | null>(null);
  const [studentNo, setStudentNo] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isSubmitEnabled = useMemo(
    () =>
      userName.trim().length > 0 &&
      Boolean(college?.college_id) &&
      studentNo.trim().length > 0,
    [userName, college, studentNo]
  );

  const resetAll = () => {
    setStep('input');
    setUserName('');
    setCollege(null);
    setStudentNo('');
    setMaskedEmail('');
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleStudentNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
    setStudentNo(onlyNumbers);
  };

  const handleSubmit = async () => {
    if (!isSubmitEnabled || isLoading) return;

    setIsLoading(true);
    try {
      const { data } = await postRecoverEmail({
        student_name: userName.trim(),
        student_no: studentNo.trim(),
        student_college: college!.college_id,
      });

      if (data?.rc === 1 && data?.masked_email) {
        setMaskedEmail(data.masked_email);
        setStep('success');
      } else {
        setStep('notfound');
      }
    } catch (e) {
      onFail('아이디 찾기에 실패하였습니다.', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToLogin = () => {
    handleClose();
  };

  const handleGoToPasswordFind = () => {
    resetAll();
    onClose();
    onSwitchToPasswordFind?.();
  };

  const handleRetry = () => {
    setStep('input');
  };

  const handleChannelTalk = () => {
    window.open('https://vd48q.channel.io/lounge');
  };

  const handleGoToSignUp = () => {
    handleClose();
    navigate('/signup');
  };

  const outlinedEnrSx = {
    backgroundColor: '#fff',
    color: primaryDark,
    border: `1px solid ${primaryDark}`,
    '&:hover': {
      color: primaryDark,
      backgroundColor: '#E7EFFF',
      border: `1px solid ${primaryDark}`,
    },
  };

  /** DialogContent 전역 버튼 오버라이드(theme)보다 우선 — contained 파란 배경 */
  const containedEnrSx = {
    '&&': {
      backgroundColor: primaryDark,
      color: '#E7EFFF',
      '&:hover': {
        backgroundColor: '#E7EFFF',
        color: primaryDark,
      },
    },
  };

  const renderInputStep = () => (
    <>
      <Box
        sx={{
          backgroundColor: primaryLight,
          borderRadius: 1.5,
          p: 2,
          mb: 3,
        }}
      >
        <Typography sx={{ fontSize: 14, color: '#1F2937', mb: 0.5 }}>
          홈페이지만 가입하셨나요?
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#1F2937', mb: 1.5, lineHeight: 1.6 }}>
          개인정보 보안을 위해 ENR 별도 가입이 필요합니다.
          <br />
          ENR 계정이 없으시다면 아이디를 찾을 수 없어요.
        </Typography>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleGoToSignUp}
          sx={{
            fontWeight: 700,
            ...outlinedEnrSx,
          }}
        >
          Smart ENR 회원가입
        </Button>
      </Box>

      <Stack spacing={2}>
        <Form.Item label="이름">
          <TextField
            fullWidth
            size="small"
            placeholder="이름을 입력해주세요."
            value={userName}
            onChange={e => setUserName(e.target.value)}
            autoComplete="off"
          />
        </Form.Item>

        <Form.Item label="학교명">
          <MuiAutocomplete
            listKey="college_lists"
            valueKey="college_id"
            size="small"
            hideSearchAdornment
            noOptionsText="다른 학교를 입력해주세요"
            placeholder="학교명을 입력 또는 선택해주세요."
            value={college}
            onChange={(value: ICollegeOption | null) => setCollege(value)}
            getApi={getCollegeList}
            getOptionLabel={(option: ICollegeOption) => option.college_name}
          />
        </Form.Item>

        <Form.Item label="학번">
          <TextField
            fullWidth
            size="small"
            placeholder="학번을 입력해주세요."
            value={studentNo}
            onChange={handleStudentNoChange}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            autoComplete="off"
          />
        </Form.Item>

        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          loading={isLoading}
          disabled={!isSubmitEnabled}
          onClick={handleSubmit}
          sx={{ mt: 1, mb: 2, ...containedEnrSx }}
        >
          이메일 확인하기
        </LoadingButton>
      </Stack>
    </>
  );

  const renderSuccessStep = () => (
    <>
      <Box
        sx={{
          border: '1px solid #D1D5DB',
          borderRadius: 1.5,
          py: 3.5,
          px: 2,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 500, color: '#1F2937' }}>
          {maskedEmail}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <Button
          fullWidth
          size="large"
          variant="outlined"
          onClick={handleGoToPasswordFind}
          sx={outlinedEnrSx}
        >
          비밀번호 찾기
        </Button>
        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={handleGoToLogin}
          sx={containedEnrSx}
        >
          로그인 하기
        </Button>
      </Stack>
    </>
  );

  const renderNotFoundStep = () => (
    <>
      <Box
        sx={{
          backgroundColor: '#FDECEC',
          borderRadius: 1.5,
          py: 3.5,
          px: 2,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            backgroundColor: '#F8C9C9',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
          }}
        >
          <ErrorOutlineIcon sx={{ color: '#D9322B', fontSize: 32 }} />
        </Box>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: '#1F2937',
            mb: 1,
          }}
        >
          일치하는 계정을 찾지 못했어요
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#4B5563', lineHeight: 1.6 }}>
          입력하신 정보와 일치하는
          <br />
          가입 이메일이 없습니다.
        </Typography>
      </Box>

      <Stack spacing={1.5} sx={{ mb: 1 }}>
        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={handleRetry}
          sx={containedEnrSx}
        >
          다시 입력하기
        </Button>
        <Button
          fullWidth
          size="large"
          variant="outlined"
          onClick={handleChannelTalk}
          sx={outlinedEnrSx}
        >
          채널톡 문의하기
        </Button>
      </Stack>

      <Box sx={{ textAlign: 'center', mt: 1.5, mb: 1 }}>
        <Link
          component="button"
          type="button"
          underline="always"
          onClick={handleGoToSignUp}
          sx={{ fontSize: 13, color: '#4B5563' }}
        >
          ENR 회원가입 후 이용해주세요.
        </Link>
      </Box>
    </>
  );

  const dialogTitle = step === 'success' ? '가입된 이메일' : '아이디 찾기';

  return (
    <Dialog
      maxWidth={maxWidth}
      fullWidth
      open={isOpen}
      onClose={handleClose}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <DialogActions sx={{ p: 0 }}>
          <Button
            variant="text"
            onClick={handleClose}
            sx={{ minWidth: 0, marginRight: '-20px', color: 'text.primary' }}
          >
            <CloseIcon />
          </Button>
        </DialogActions>
        <Typography sx={{ fontSize: '24px', fontWeight: 700 }}>
          {dialogTitle}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {step === 'input' && renderInputStep()}
        {step === 'success' && renderSuccessStep()}
        {step === 'notfound' && renderNotFoundStep()}
      </DialogContent>
    </Dialog>
  );
}

export default Confirm;
